<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { OnClickOutside } from '@vueuse/components';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  VueFlow,
  Handle,
  Position,
  MarkerType,
  useVueFlow,
} from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/minimap/dist/style.css';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NodePicker from './NodePicker.vue';
import FlowEdge from './FlowEdge.vue';
import VariablePicker from './VariablePicker.vue';
import VariableComboBox from './VariableComboBox.vue';
import VariablesPanel from './VariablesPanel.vue';
import NodeTargetPicker from './NodeTargetPicker.vue';
import FlowsAPI from 'dashboard/api/flows';
import {
  collectVariableUsage,
  renameVariableInNodes,
  clearVariableInNodes,
  variableReference,
} from './variableHelpers';

const NODE_TYPES = {
  send_message: { fields: ['content', 'next'] },
  ask_choice: { fields: ['prompt', 'options', 'variable'] },
  wait_for_input: { fields: ['variable', 'next'] },
  collect_fields: { fields: ['collected', 'missing_message', 'next'] },
  http_request: { fields: ['url', 'prefix', 'map', 'auth', 'timeout'] },
  set_variable: { fields: ['key', 'value', 'next'] },
  set_attribute: { fields: ['target', 'key', 'value', 'next'] },
  condition: { fields: ['variable', 'cases', 'default'] },
  delay: { fields: ['seconds', 'next'] },
  add_labels: { fields: ['labels', 'next'] },
  add_contact_note: { fields: ['note', 'next'] },
  add_private_note: { fields: ['note', 'next'] },
  update_contact: { fields: ['field', 'value', 'next'] },
  resolve_conversation: { fields: ['next'] },
  handoff: {
    fields: [
      'team_id',
      'team',
      'assignee_id',
      'assignee_email',
      'reason',
      'unavailable_next',
    ],
  },
  switch_brain: { fields: ['agent_bot_id', 'keep_variables'] },
  end: { fields: [] },
  jump: { fields: ['next'] },
};

const DEFAULT_DATA = {
  send_message: { content: '', next: '' },
  ask_choice: { prompt: '', options: [], variable: '' },
  wait_for_input: { variable: '', next: '' },
  collect_fields: { fields: [], missing_message: '', next: '' },
  http_request: {
    url: '',
    prefix: '',
    map: {},
    auth: '',
    timeout: null,
    next: '',
    not_found_next: '',
    error_next: '',
  },
  set_variable: { key: '', value: '', next: '' },
  set_attribute: { target: 'contact', key: '', value: '', next: '' },
  condition: { variable: '', cases: [], default: '' },
  delay: { seconds: 5, next: '' },
  add_labels: { labels: [], next: '' },
  add_contact_note: { note: '', next: '' },
  add_private_note: { note: '', next: '' },
  update_contact: { field: 'name', value: '', next: '' },
  resolve_conversation: { next: '' },
  handoff: {
    team_id: '',
    team: '',
    assignee_id: '',
    assignee_email: '',
    reason: '',
    unavailable_next: '',
  },
  switch_brain: { agent_bot_id: '', keep_variables: [] },
  end: {},
  jump: { next: '' },
};

const NODE_TYPE_ICONS = {
  send_message: 'i-lucide-message-circle',
  ask_choice: 'i-lucide-list-checks',
  wait_for_input: 'i-lucide-keyboard',
  collect_fields: 'i-lucide-clipboard-list',
  http_request: 'i-lucide-globe',
  set_variable: 'i-lucide-variable',
  set_attribute: 'i-lucide-tag',
  condition: 'i-lucide-git-branch',
  delay: 'i-lucide-clock',
  add_labels: 'i-lucide-tags',
  add_contact_note: 'i-lucide-sticky-note',
  add_private_note: 'i-lucide-lock',
  update_contact: 'i-lucide-user-pen',
  resolve_conversation: 'i-lucide-circle-check',
  handoff: 'i-lucide-user-check',
  switch_brain: 'i-lucide-shuffle',
  end: 'i-lucide-flag',
  jump: 'i-lucide-corner-down-right',
};

// Typebot-style block coloring: one accent per node type, grouped into the same
// bubbles/inputs/logic/end families Typebot itself uses for its block picker.
const NODE_TYPE_COLORS = {
  send_message: {
    text: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500',
  },
  ask_choice: {
    text: 'text-violet-500',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500',
  },
  wait_for_input: {
    text: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500',
  },
  collect_fields: {
    text: 'text-sky-500',
    bg: 'bg-sky-500/10',
    border: 'border-sky-500',
  },
  http_request: {
    text: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500',
  },
  set_variable: {
    text: 'text-amber-500',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500',
  },
  set_attribute: {
    text: 'text-orange-500',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500',
  },
  condition: {
    text: 'text-pink-500',
    bg: 'bg-pink-500/10',
    border: 'border-pink-500',
  },
  delay: {
    text: 'text-slate-500',
    bg: 'bg-slate-500/10',
    border: 'border-slate-500',
  },
  jump: {
    text: 'text-indigo-500',
    bg: 'bg-indigo-500/10',
    border: 'border-indigo-500',
  },
  add_labels: {
    text: 'text-lime-500',
    bg: 'bg-lime-500/10',
    border: 'border-lime-500',
  },
  add_contact_note: {
    text: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500',
  },
  add_private_note: {
    text: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500',
  },
  update_contact: {
    text: 'text-orange-500',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500',
  },
  resolve_conversation: {
    text: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500',
  },
  handoff: {
    text: 'text-red-500',
    bg: 'bg-red-500/10',
    border: 'border-red-500',
  },
  switch_brain: {
    text: 'text-teal-500',
    bg: 'bg-teal-500/10',
    border: 'border-teal-500',
  },
  end: {
    text: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500',
  },
};

const NODE_CATEGORIES = [
  { key: 'bubbles', types: ['send_message'] },
  { key: 'inputs', types: ['ask_choice', 'wait_for_input', 'collect_fields'] },
  {
    key: 'logic',
    types: [
      'http_request',
      'set_variable',
      'set_attribute',
      'condition',
      'delay',
      'jump',
      'switch_brain',
    ],
  },
  {
    key: 'crm',
    types: [
      'add_labels',
      'add_contact_note',
      'add_private_note',
      'update_contact',
    ],
  },
  { key: 'end', types: ['resolve_conversation', 'handoff', 'end'] },
];

// Os operadores que Flows::ConditionEvaluator conhece. A lista existe dos dois
// lados de propósito: aqui para oferecer, lá para recusar o que não existe.
const UNARY_OPERATORS = ['blank', 'present'];

const MULTI_HANDLE_TYPES = ['ask_choice', 'condition'];

// Mirrors Bots::Brains::Flow::WAIT_NODE_TYPES - the blocks that park the session on a reply, and
// so the only ones a deadline makes sense on.
const WAIT_NODE_TYPES = ['ask_choice', 'wait_for_input', 'collect_fields'];

// Mirrors the shapes Flows::FieldExtractor validates: only these can arrive and be turned down,
// which is the only case an "it does not check out" sentence has to say anything about.
const CHECKED_FIELD_TYPES = ['cpf', 'cnpj', 'email', 'phone'];

const NODE_TYPE_DESCRIPTION_KEYS = {
  send_message: 'SEND_MESSAGE',
  ask_choice: 'ASK_CHOICE',
  wait_for_input: 'WAIT_FOR_INPUT',
  collect_fields: 'COLLECT_FIELDS',
  http_request: 'HTTP_REQUEST',
  set_variable: 'SET_VARIABLE',
  set_attribute: 'SET_ATTRIBUTE',
  condition: 'CONDITION',
  delay: 'DELAY',
  add_labels: 'ADD_LABELS',
  add_contact_note: 'ADD_CONTACT_NOTE',
  add_private_note: 'ADD_PRIVATE_NOTE',
  update_contact: 'UPDATE_CONTACT',
  resolve_conversation: 'RESOLVE_CONVERSATION',
  handoff: 'HANDOFF',
  switch_brain: 'SWITCH_BRAIN',
  end: 'END',
  jump: 'JUMP',
};

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const operatorOptions = computed(() => [
  { value: 'equals', label: t('FLOWS.EDITOR.OPERATORS.EQUALS') },
  { value: 'not_equals', label: t('FLOWS.EDITOR.OPERATORS.NOT_EQUALS') },
  { value: 'contains', label: t('FLOWS.EDITOR.OPERATORS.CONTAINS') },
  { value: 'not_contains', label: t('FLOWS.EDITOR.OPERATORS.NOT_CONTAINS') },
  { value: 'starts_with', label: t('FLOWS.EDITOR.OPERATORS.STARTS_WITH') },
  { value: 'ends_with', label: t('FLOWS.EDITOR.OPERATORS.ENDS_WITH') },
  { value: 'blank', label: t('FLOWS.EDITOR.OPERATORS.BLANK') },
  { value: 'present', label: t('FLOWS.EDITOR.OPERATORS.PRESENT') },
  { value: 'gt', label: t('FLOWS.EDITOR.OPERATORS.GT') },
  { value: 'gte', label: t('FLOWS.EDITOR.OPERATORS.GTE') },
  { value: 'lt', label: t('FLOWS.EDITOR.OPERATORS.LT') },
  { value: 'lte', label: t('FLOWS.EDITOR.OPERATORS.LTE') },
]);

// O rótulo do ramo no canvas: com igualdade, o valor sozinho já se lê ("limite");
// com qualquer outro operador é o operador que carrega o sentido ("> 0").
const caseLabel = branch => {
  const operator = branch.operator || 'equals';
  const symbol = { gt: '>', gte: '>=', lt: '<', lte: '<=' }[operator];
  if (UNARY_OPERATORS.includes(operator))
    return operatorOptions.value.find(o => o.value === operator)?.label;
  const value = 'value' in branch ? branch.value : branch.equals;
  if (operator === 'equals') return value;
  return `${symbol || operator} ${value}`;
};
const { accountScopedRoute } = useAccount();

const flowId = computed(() => route.params.flowId);
const versions = useMapGetter('flows/getVersions');
const versionUiFlags = useMapGetter('flows/getVersionUIFlags');

const isLoading = ref(true);
const graph = ref(null);
const workingVersionId = ref(null);
// 'idle' (nothing loaded yet) | 'unsaved' | 'saving' | 'saved' | 'error'
const saveStatus = ref('idle');
const selectedNodeId = ref(null);

const currentFlow = computed(() =>
  store.getters['flows/getFlow'](flowId.value)
);
const flowVersions = computed(() => versions.value(flowId.value));

const draftVersion = computed(() =>
  flowVersions.value.find(v => !v.published_at)
);
const publishedVersion = computed(() =>
  flowVersions.value.find(v => v.id === currentFlow.value.current_version_id)
);

const nodeTypeLabel = type =>
  t(`FLOWS.EDITOR.NODE_TYPES.${type.toUpperCase()}`);

const nodeTypeDescription = type =>
  t(`FLOWS.EDITOR.NODE_TYPE_DESCRIPTIONS.${NODE_TYPE_DESCRIPTION_KEYS[type]}`);

const categoryLabel = key => t(`FLOWS.EDITOR.CATEGORIES.${key.toUpperCase()}`);

const buildPickerCategories = excludedTypes =>
  NODE_CATEGORIES.map(category => ({
    key: category.key,
    label: categoryLabel(category.key),
    items: category.types
      .filter(type => !excludedTypes.includes(type))
      .map(type => ({
        type,
        label: nodeTypeLabel(type),
        description: nodeTypeDescription(type),
        icon: NODE_TYPE_ICONS[type],
        colors: NODE_TYPE_COLORS[type],
      })),
  })).filter(category => category.items.length);

// Every block type - used by the always-available "add block" picker.
const fullPickerCategories = computed(() => buildPickerCategories([]));

// Inserting a block in the middle of an existing connection only makes sense for a block with
// exactly one outgoing next: ask_choice/condition need the user to wire each option/case by hand,
// and switch_brain/handoff/end are terminal (no "next" to hand off to the rest of the line at
// all).
const edgeInsertPickerCategories = computed(() =>
  buildPickerCategories([
    ...MULTI_HANDLE_TYPES,
    'jump',
    'switch_brain',
    'handoff',
    'end',
  ])
);

const teams = useMapGetter('teams/getTeams');
const verifiedAgents = useMapGetter('agents/getVerifiedAgents');
const agentBots = useMapGetter('agentBots/getBots');

// Only the bot types the session runtime can run (Bots::BrainResolver::BRAINS). A webhook bot
// never gets a session, so handing one a conversation would drop it.
const switchTargetBots = computed(() =>
  (agentBots.value || []).filter(
    bot => bot.bot_type === 'flow' || bot.bot_type === 'ai_agent'
  )
);

// A deadline routes the flow somewhere without drawing a line, same as a jump - so the card has
// to say it, otherwise that branch is invisible on the canvas.
const timeoutPreview = data => {
  if (!data.timeout_seconds) return '';
  return t('FLOWS.EDITOR.FIELDS.TIMEOUT_PREVIEW', {
    minutes: Math.round(data.timeout_seconds / 60),
  });
};

// Every block's preview but the jump's. A jump describes where it goes, which needs the
// destination's own name, and a name falls back to a preview when the block has no title - so a
// jump able to name itself through the block it points at would describe itself in a circle. It
// is added back in NODE_PREVIEW_BUILDERS below, once naming exists.
const NODE_CONTENT_PREVIEW_BUILDERS = {
  send_message: data => data.content,
  ask_choice: data =>
    [
      [data.prompt, (data.options || []).map(o => o.title).join(' · ')]
        .filter(Boolean)
        .join(' — '),
      timeoutPreview(data),
    ]
      .filter(Boolean)
      .join('\n'),
  wait_for_input: data =>
    [data.variable ? `→ ${data.variable}` : '', timeoutPreview(data)]
      .filter(Boolean)
      .join('\n'),
  collect_fields: data =>
    [
      (data.fields || [])
        .map(field => (field.ask_next ? `${field.variable} →` : field.variable))
        .join(' · '),
      timeoutPreview(data),
    ]
      .filter(Boolean)
      .join('\n'),
  // O host basta para reconhecer o bloco; a URL inteira não cabe no cartão.
  http_request: data => {
    if (!data.url) return '';
    const host = data.url.replace(/^https?:\/\//, '').split('/')[0];
    return [host, data.prefix ? `→ ${data.prefix}*` : '']
      .filter(Boolean)
      .join('\n');
  },
  set_variable: data => (data.key ? `${data.key} = ${data.value ?? ''}` : ''),
  set_attribute: data =>
    data.key ? `${data.target}.${data.key} = ${data.value ?? ''}` : '',
  condition: data => (data.variable ? `? ${data.variable}` : ''),
  delay: data => (data.seconds ? `${data.seconds}s` : ''),
  handoff: data => {
    const team = (teams.value || []).find(item => item.id === data.team_id);
    const agent = (verifiedAgents.value || []).find(
      a => a.id === data.assignee_id
    );
    return [team?.name || data.team, agent?.name || data.assignee_email]
      .filter(Boolean)
      .join(' · ');
  },
  add_labels: data => (data.labels || []).join(' · '),
  add_contact_note: data => data.note,
  add_private_note: data => data.note,
  update_contact: data => (data.value ? `${data.field} = ${data.value}` : ''),
  resolve_conversation: () => '',
  switch_brain: data => {
    const bot = (switchTargetBots.value || []).find(
      b => b.id === data.agent_bot_id
    );
    return bot ? `→ ${bot.name}` : '';
  },
  end: () => '',
};

// What a block says about itself, which is what it is called when it has no title.
const nodeContentPreview = node =>
  (NODE_CONTENT_PREVIEW_BUILDERS[node.type]?.(node.data || {}) || '').trim();

// A node card is 9rem wide and clamps at two lines, so a destination printed on one gets cut
// short. The picker itself has room for the whole thing.
const CARD_LABEL_LIMIT = 22;

const truncateLabel = (text, limit) =>
  text.length > limit ? `${text.slice(0, limit).trim()}…` : text;

// Position among the nodes of the same type, so two blocks that say nothing yet are still
// distinguishable without showing the internal id.
const nodeOrdinals = computed(() => {
  const seen = {};
  const ordinals = {};
  (graph.value?.nodes || []).forEach(node => {
    seen[node.type] = (seen[node.type] || 0) + 1;
    ordinals[node.id] = seen[node.type];
  });
  return ordinals;
});

// What a block would be called if the user never named it.
const nodeAutoName = node =>
  nodeContentPreview(node) ||
  `${nodeTypeLabel(node.type)} ${nodeOrdinals.value[node.id] ?? ''}`.trim();

// What a block is called everywhere in the UI. The node id is internal and never shown.
const nodeDisplayName = node => node.title?.trim() || nodeAutoName(node);

const usesFallbackName = node =>
  !node.title?.trim() && !nodeContentPreview(node);

// The whole preview, jump included: everything a card or a picker shows.
const NODE_PREVIEW_BUILDERS = {
  ...NODE_CONTENT_PREVIEW_BUILDERS,
  jump: data => {
    const target = graph.value?.nodes.find(node => node.id === data.next);
    // Named by what the destination says rather than by its type: this card is narrow and
    // already prints its own type label right above the preview.
    return target
      ? `→ ${truncateLabel(nodeDisplayName(target), CARD_LABEL_LIMIT)}`
      : '';
  },
};

const nodePreview = node =>
  NODE_PREVIEW_BUILDERS[node.type]?.(node.data || {}) || '';

const nodeTypeOptions = computed(() =>
  Object.keys(NODE_TYPES).map(type => ({
    value: type,
    label: nodeTypeLabel(type),
  }))
);

// Jump is the only block that still picks its destination from a list - every other connection
// is drawn on the canvas. The jump's own node is left out: jumping to itself is a loop with no
// way out.
const jumpTargets = computed(() =>
  (graph.value?.nodes || [])
    .filter(node => node.id !== selectedNodeId.value)
    .map(node => ({
      id: node.id,
      name: nodeDisplayName(node),
      // A block that fell back to "<type> <n>" would only repeat itself in the subtitle, so it
      // shows what it says instead - or says plainly that it says nothing yet.
      subtitle: usesFallbackName(node)
        ? nodePreview(node).trim() || t('FLOWS.EDITOR.FIELDS.NODE_EMPTY')
        : nodeTypeLabel(node.type),
      icon: NODE_TYPE_ICONS[node.type],
      colors: NODE_TYPE_COLORS[node.type],
    }))
);

const selectedNode = computed(() =>
  graph.value?.nodes.find(n => n.id === selectedNodeId.value)
);

const variableUsage = computed(() =>
  collectVariableUsage(graph.value?.nodes || [])
);

const nodeReference = id => {
  const node = graph.value?.nodes.find(n => n.id === id);
  return {
    id,
    name: node ? nodeDisplayName(node) : id,
    typeLabel: node ? nodeTypeLabel(node.type) : '',
  };
};

// Declared (graph.variables) union whatever the nodes actually reference, each annotated with
// the blocks that write it and the blocks that read it.
const allVariables = computed(() =>
  [
    ...new Set([
      ...(graph.value?.variables || []).map(v => v.key),
      ...Object.keys(variableUsage.value),
    ]),
  ].map(key => {
    const { writtenIn = [], readIn = [] } = variableUsage.value[key] || {};
    return {
      key,
      writtenIn: writtenIn.map(nodeReference),
      readIn: readIn.map(nodeReference),
      usedIn: [...new Set([...writtenIn, ...readIn])],
    };
  })
);

const addVariable = key => {
  if (!key || (graph.value.variables || []).some(v => v.key === key)) return;
  graph.value.variables = [...(graph.value.variables || []), { key }];
};

const renameVariable = ({ from, to }) => {
  graph.value.variables = (graph.value.variables || []).map(v =>
    v.key === from ? { ...v, key: to } : v
  );
  renameVariableInNodes(graph.value.nodes, from, to);
};

// Drops the declaration *and* every reference: leaving the references behind would just make
// the variable reappear, since the list is built from what the nodes reference. Recoverable
// with the editor's own undo (Ctrl+Z) like any other graph edit.
const deleteVariable = key => {
  graph.value.variables = (graph.value.variables || []).filter(
    v => v.key !== key
  );
  clearVariableInNodes(graph.value.nodes, key);
};

// Which of the 4 interpolation-eligible fields (content/prompt/value) currently has its
// {{}} picker open on the selected node - null when none is open.
const activeVariableField = ref(null);

const openVariablePicker = field => {
  activeVariableField.value =
    activeVariableField.value === field ? null : field;
};

// Each interpolation-eligible field keeps a ref to its component so the reference lands at the
// caret instead of at the end of whatever the user already typed.
const contentFieldRef = ref(null);
const promptFieldRef = ref(null);
const valueFieldRef = ref(null);

const fieldElement = field => {
  const component = {
    content: contentFieldRef,
    prompt: promptFieldRef,
    value: valueFieldRef,
  }[field]?.value;
  return component?.textareaRef || component?.inputRef || null;
};

const insertVariableIntoField = key => {
  const field = activeVariableField.value;
  if (!field || !selectedNode.value) return;

  const current = selectedNode.value.data[field] || '';
  const insertion = variableReference(key);
  const element = fieldElement(field);
  const start = element?.selectionStart ?? current.length;
  const end = element?.selectionEnd ?? current.length;
  const caret = start + insertion.length;

  selectedNode.value.data[field] =
    current.slice(0, start) + insertion + current.slice(end);
  addVariable(key);
  activeVariableField.value = null;

  nextTick(() => {
    if (!element) return;
    element.focus();
    element.setSelectionRange(caret, caret);
  });
};

const showVariablesPanel = ref(false);
const toggleVariablesPanel = () => {
  showVariablesPanel.value = !showVariablesPanel.value;
};

const nextRefsOf = node => {
  const data = node.data || {};
  switch (node.type) {
    // Its own `next` plus one branch per field that is asked somewhere else - a branch drawn
    // nowhere is a branch nobody remembers exists.
    case 'collect_fields':
      return [
        { target: data.next, label: '', handleId: null },
        ...(data.fields || []).flatMap((field, i) =>
          field.ask_next
            ? [
                {
                  target: field.ask_next,
                  label: field.variable,
                  handleId: `field-${i}`,
                },
              ]
            : []
        ),
      ];
    case 'ask_choice':
      return (data.options || []).map((o, i) => ({
        target: o.next,
        label: o.title,
        handleId: `option-${i}`,
      }));
    case 'condition':
      return [
        ...(data.cases || []).map((c, i) => ({
          target: c.next,
          label: caseLabel(c),
          handleId: `case-${i}`,
        })),
        ...(data.default
          ? [
              {
                target: data.default,
                label: t('FLOWS.EDITOR.DEFAULT_CASE'),
                handleId: 'default',
              },
            ]
          : []),
      ];
    // A handoff only branches when it has somewhere to go with nobody available; otherwise it is
    // terminal like it always was.
    case 'http_request':
      return [
        {
          target: data.next,
          label: t('FLOWS.EDITOR.FIELDS.HTTP_OK'),
          handleId: null,
        },
        {
          target: data.not_found_next,
          label: t('FLOWS.EDITOR.FIELDS.HTTP_NOT_FOUND'),
          handleId: 'not_found',
        },
        {
          target: data.error_next,
          label: t('FLOWS.EDITOR.FIELDS.HTTP_ERROR'),
          handleId: 'error',
        },
      ];
    case 'handoff':
      return data.unavailable_next
        ? [
            {
              target: data.unavailable_next,
              label: t('FLOWS.EDITOR.FIELDS.HANDOFF_UNAVAILABLE_LABEL'),
              handleId: 'unavailable',
            },
          ]
        : [];
    case 'jump':
    case 'switch_brain':
    case 'end':
      return [];
    default:
      return data.next
        ? [{ target: data.next, label: '', handleId: null }]
        : [];
  }
};

// Layout follows jumps as well, even though a jump draws no line: a node reached only through
// one still deserves a position next to what leads there, not the orphan column at the end.
const layoutTargetsOf = node =>
  node.type === 'jump'
    ? [node.data?.next].filter(Boolean)
    : nextRefsOf(node)
        .map(edgeRef => edgeRef.target)
        .filter(Boolean);

// Fallback layout only: seeds a position for nodes the user hasn't dragged yet (BFS depth from
// start). Once a node has a persisted `position`, this is never consulted for it again.
const autoLayoutPositions = computed(() => {
  if (!graph.value) return {};
  const depths = {};
  if (graph.value.start) {
    const queue = [[graph.value.start, 0]];
    const visited = new Set();
    while (queue.length) {
      const [id, d] = queue.shift();
      if (!visited.has(id)) {
        visited.add(id);
        depths[id] = d;
        const node = graph.value.nodes.find(n => n.id === id);
        if (node) {
          layoutTargetsOf(node).forEach(target => {
            if (!visited.has(target)) {
              queue.push([target, d + 1]);
            }
          });
        }
      }
    }
  }
  const maxDepth = Object.values(depths).reduce((m, d) => Math.max(m, d), 0);
  const byDepth = {};
  graph.value.nodes.forEach(n => {
    const d = depths[n.id] ?? maxDepth + 1;
    byDepth[d] = byDepth[d] || [];
    byDepth[d].push(n.id);
  });
  const positions = {};
  Object.entries(byDepth).forEach(([d, ids]) => {
    ids.forEach((id, i) => {
      positions[id] = { x: Number(d) * 280, y: i * 160 };
    });
  });
  return positions;
});

const canvasWrapperRef = ref(null);

// The settings page shell this editor lives in doesn't stretch its router-view to fill the
// viewport, so a CSS-only "100vh minus a guessed header height" is never quite right. Measuring
// the canvas's own offset and filling everything below it works regardless of how tall whatever
// sits above happens to be.
const canvasHeight = ref('600px');

const updateCanvasHeight = () => {
  if (!canvasWrapperRef.value) return;
  const top = canvasWrapperRef.value.getBoundingClientRect().top;
  canvasHeight.value = `${Math.max(window.innerHeight - top - 8, 500)}px`;
};

// A plain resize listener misses layout shifts that aren't a window resize (sidebar counts
// loading in, web fonts swapping, the settings header's own content changing) - any of those
// moves the canvas's top offset without firing 'resize'. Watching the whole body's box catches
// all of them without needing to know which one actually happened.
let bodyResizeObserver = null;

onMounted(() => {
  window.addEventListener('resize', updateCanvasHeight);
  bodyResizeObserver = new ResizeObserver(() => updateCanvasHeight());
  bodyResizeObserver.observe(document.body);
});
onUnmounted(() => {
  window.removeEventListener('resize', updateCanvasHeight);
  bodyResizeObserver?.disconnect();
});

// The settings shell's own layout (overflow-hidden wrappers, no defined height chain - see
// above) made a CSS-only `position: fixed; inset: 0` fullscreen unreliable: it ended up clipped
// to the shell's own small box instead of the real viewport. The browser's native Fullscreen API
// takes the element out of that layout entirely, so it isn't affected by any of that.
const isFullscreen = ref(false);

const toggleFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else {
    canvasWrapperRef.value?.requestFullscreen();
  }
};

const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement;
  nextTick(updateCanvasHeight);
};

onMounted(() =>
  document.addEventListener('fullscreenchange', handleFullscreenChange)
);
onUnmounted(() =>
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
);

const showSidePanel = ref(true);
const toggleSidePanel = () => {
  showSidePanel.value = !showSidePanel.value;
};

watch(
  () => !!graph.value,
  hasGraph => {
    if (!hasGraph) return;
    nextTick(updateCanvasHeight);
    // Belt and suspenders: re-measure a moment later in case something above the canvas (the
    // settings header, the left nav) is still settling its own size right after this mounts.
    setTimeout(updateCanvasHeight, 150);
    setTimeout(updateCanvasHeight, 600);
  }
);

// Undo/redo over the whole graph. Snapshots are debounced so a burst of edits (dragging a node,
// typing in a field) collapses into a single undo step instead of one per reactive tick.
const UNDO_DEBOUNCE_MS = 500;
const MAX_HISTORY = 50;

const undoStack = ref([]);
const redoStack = ref([]);
const isRestoringHistory = ref(false);
let lastSnapshot = null;
let historyDebounceTimer = null;

const resetHistory = () => {
  clearTimeout(historyDebounceTimer);
  undoStack.value = [];
  redoStack.value = [];
  lastSnapshot = graph.value ? JSON.stringify(graph.value) : null;
};

watch(
  graph,
  () => {
    if (isRestoringHistory.value || !graph.value) return;
    clearTimeout(historyDebounceTimer);
    historyDebounceTimer = setTimeout(() => {
      const snapshot = JSON.stringify(graph.value);
      if (snapshot === lastSnapshot) return;
      undoStack.value.push(lastSnapshot);
      if (undoStack.value.length > MAX_HISTORY) undoStack.value.shift();
      redoStack.value = [];
      lastSnapshot = snapshot;
    }, UNDO_DEBOUNCE_MS);
  },
  { deep: true }
);

const restoreSnapshot = snapshot => {
  isRestoringHistory.value = true;
  graph.value = JSON.parse(snapshot);
  lastSnapshot = snapshot;
  nextTick(() => {
    isRestoringHistory.value = false;
  });
};

const undo = () => {
  clearTimeout(historyDebounceTimer);
  const previous = undoStack.value.pop();
  if (previous === undefined) return;
  redoStack.value.push(lastSnapshot);
  restoreSnapshot(previous);
};

const redo = () => {
  clearTimeout(historyDebounceTimer);
  const next = redoStack.value.pop();
  if (next === undefined) return;
  undoStack.value.push(lastSnapshot);
  restoreSnapshot(next);
};

const handleHistoryKeydown = event => {
  const isTyping = ['INPUT', 'TEXTAREA'].includes(
    document.activeElement?.tagName
  );
  if (isTyping || !(event.ctrlKey || event.metaKey)) return;
  if (event.key.toLowerCase() !== 'z') return;

  event.preventDefault();
  if (event.shiftKey) {
    redo();
  } else {
    undo();
  }
};

onMounted(() => window.addEventListener('keydown', handleHistoryKeydown));
onUnmounted(() => window.removeEventListener('keydown', handleHistoryKeydown));

// An option used to be able to carry a "stored value" different from the label the customer
// sees - two names for one thing, when the runtime only ever matches on the label anyway
// (Bots::Channel::ReplyParser has no native-payload tier). Options are label-only now, so a
// graph written under the old model gets collapsed on load: the value follows the label, and
// any condition that branched on the old value follows with it, otherwise that branch would
// quietly stop matching. `value` itself stays in the graph - Bots::Actions::AskChoice and
// Bots::Brains::Flow still read it.
const collapseOptionValues = candidateGraph => {
  candidateGraph.nodes
    .filter(node => node.type === 'ask_choice')
    .forEach(node => {
      const renamed = {};
      (node.data?.options || []).forEach(option => {
        if (option.value === option.title) return;
        if (option.value) renamed[option.value] = option.title;
        option.value = option.title;
      });

      if (!node.data?.variable || !Object.keys(renamed).length) return;
      candidateGraph.nodes
        .filter(
          n => n.type === 'condition' && n.data?.variable === node.data.variable
        )
        .forEach(n =>
          (n.data.cases || []).forEach(branch => {
            if (branch.value in renamed) branch.value = renamed[branch.value];
          })
        );
    });
};

// `equals` era o nome do lado direito quando o nó só sabia comparar igualdade.
// Um grafo antigo abre no editor já na forma nova - a versão publicada não muda,
// porque publicar tira um retrato; o que o autor salvar daqui é que nasce assim.
const normalizeConditionCases = candidateGraph => {
  candidateGraph.nodes
    .filter(node => node.type === 'condition')
    .forEach(node =>
      (node.data?.cases || []).forEach(branch => {
        if (branch.operator && 'value' in branch) return;
        branch.operator = branch.operator || 'equals';
        branch.value = 'value' in branch ? branch.value : branch.equals;
        delete branch.equals;
      })
    );
};

const loadGraphFromVersion = version => {
  if (!version) {
    graph.value = null;
    workingVersionId.value = null;
    saveStatus.value = 'idle';
    return;
  }
  const loaded = JSON.parse(
    JSON.stringify(version.graph || { start: '', nodes: [] })
  );
  normalizeConditionCases(loaded);
  collapseOptionValues(loaded);
  loaded.variables = loaded.variables || [];
  graph.value = loaded;
  workingVersionId.value = version.id;
  selectedNodeId.value = null;
  saveStatus.value = 'saved';
  resetHistory();
};

const refresh = async () => {
  isLoading.value = true;
  await Promise.all([
    store.dispatch('flows/show', flowId.value),
    store.dispatch('flows/getVersions', flowId.value),
  ]);
  isLoading.value = false;
};

onMounted(async () => {
  store.dispatch('attributes/get');
  store.dispatch('teams/get');
  store.dispatch('agents/get');
  store.dispatch('agentBots/get');
  store.dispatch('labels/get');
  await refresh();
  loadGraphFromVersion(draftVersion.value);
});

watch(draftVersion, version => {
  if (version && workingVersionId.value !== version.id) {
    loadGraphFromVersion(version);
  }
});

const createDraft = async () => {
  const seedGraph = publishedVersion.value?.graph
    ? JSON.parse(JSON.stringify(publishedVersion.value.graph))
    : {
        start: 'start',
        nodes: [
          {
            id: 'start',
            type: 'send_message',
            data: { content: '', next: 'end' },
          },
          { id: 'end', type: 'end', data: {} },
        ],
      };

  const version = await store.dispatch('flows/createVersion', {
    flowId: flowId.value,
    graph: seedGraph,
  });
  if (version) loadGraphFromVersion(version);
};

const addNode = (type, explicitPosition) => {
  const id = `node_${Math.random().toString(36).slice(2, 8)}`;
  const newNode = { id, type, data: { ...DEFAULT_DATA[type] } };

  if (explicitPosition) {
    newNode.position = explicitPosition;
  } else {
    const anchor =
      selectedNode.value?.position ||
      autoLayoutPositions.value[selectedNode.value?.id];
    if (anchor) newNode.position = { x: anchor.x + 260, y: anchor.y + 180 };
  }

  graph.value.nodes.push(newNode);
  selectedNodeId.value = id;
  return id;
};

// The side panel doubles as the node picker: plain "add a block" (pendingEdgeInsert null) or
// "insert a block into this connection" (pendingEdgeInsert holds the edge that was clicked).
const pendingEdgeInsert = ref(null);

const cancelEdgeInsert = () => {
  pendingEdgeInsert.value = null;
};

const openEdgeInsertPicker = edge => {
  pendingEdgeInsert.value = edge;
  showSidePanel.value = true;
};

// Maps a dragged/removed connection's sourceHandle id back to the data field it represents.
const handleFieldSetter = (node, handleId) => target => {
  if (node.type === 'ask_choice' && handleId?.startsWith('option-')) {
    const option = node.data.options[Number(handleId.split('-')[1])];
    if (option) option.next = target;
  } else if (node.type === 'condition' && handleId?.startsWith('case-')) {
    const caseItem = node.data.cases[Number(handleId.split('-')[1])];
    if (caseItem) caseItem.next = target;
  } else if (node.type === 'condition' && handleId === 'default') {
    node.data.default = target;
  } else if (node.type === 'http_request' && handleId === 'not_found') {
    node.data.not_found_next = target;
  } else if (node.type === 'http_request' && handleId === 'error') {
    node.data.error_next = target;
  } else if (node.type === 'handoff' && handleId === 'unavailable') {
    node.data.unavailable_next = target;
  } else if (node.type === 'collect_fields' && handleId?.startsWith('field-')) {
    const field = node.data.fields[Number(handleId.split('-')[1])];
    if (field) field.ask_next = target;
  } else {
    node.data.next = target;
  }
};

// Clearing the field the connection stands for is what deletes it - the edge is drawn from that
// field, so it disappears on the next render.
const removeEdge = edge => {
  const node = graph.value.nodes.find(n => n.id === edge.source);
  if (node) handleFieldSetter(node, edge.sourceHandle)('');
};

const EDGE_CONTEXT_MENU_ACTIONS = [
  {
    key: 'insert',
    icon: 'i-lucide-plus',
    labelKey: 'FLOWS.EDITOR.EDGE.INSERT',
    run: edge => openEdgeInsertPicker(edge),
  },
  {
    key: 'remove',
    icon: 'i-lucide-unlink',
    labelKey: 'FLOWS.EDITOR.EDGE.REMOVE',
    danger: true,
    run: edge => removeEdge(edge),
  },
];

const nodeContextMenu = ref(null);
const edgeContextMenu = ref(null);

const openEdgeContextMenu = (event, edge) => {
  event.preventDefault();
  nodeContextMenu.value = null;
  edgeContextMenu.value = {
    edge: {
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
    },
    x: event.clientX,
    y: event.clientY,
  };
};

const closeEdgeContextMenu = () => {
  edgeContextMenu.value = null;
};

const runEdgeContextMenuAction = action => {
  const menu = edgeContextMenu.value;
  closeEdgeContextMenu();
  if (menu) action.run(menu.edge);
};

// vue-flow hands the right-click over as { event, edge }.
const onEdgeContextMenu = ({ event, edge }) => openEdgeContextMenu(event, edge);

const removeNode = id => {
  graph.value.nodes = graph.value.nodes.filter(n => n.id !== id);

  // Whatever pointed at it keeps an id that no longer exists otherwise, and publishing then
  // fails naming a block nobody can find - the ids aren't shown anywhere any more.
  graph.value.nodes.forEach(node => {
    nextRefsOf(node)
      .filter(edgeRef => edgeRef.target === id)
      .forEach(edgeRef => handleFieldSetter(node, edgeRef.handleId)(''));
    // Neither of these draws a line, so nextRefsOf doesn't know about them.
    if (node.type === 'jump' && node.data.next === id) node.data.next = '';
    if (node.data?.timeout_next === id) node.data.timeout_next = '';
  });

  if (graph.value.start === id) graph.value.start = '';
  if (selectedNodeId.value === id) selectedNodeId.value = null;
};

// Editing a node's data always happens in the big modal now - the side panel is just for
// browsing/adding blocks. Both canvas clicks and the "Nós" list route through this.
// Bots::Actions::SetAttribute quietly does nothing for a key the account never defined, so the
// editor offers the defined ones instead of a free text field where a typo looks fine.
const ATTRIBUTE_MODELS = {
  contact: 'contact_attribute',
  conversation: 'conversation_attribute',
};

const customAttributes = useMapGetter('attributes/getAttributes');

const accountLabels = useMapGetter('labels/getLabels');

// Bots::Actions::UpdateContact::WRITABLE - the three fields the product treats as identity.
const CONTACT_FIELDS = ['name', 'email', 'phone_number'];

const contactFieldOptions = computed(() =>
  CONTACT_FIELDS.map(field => ({
    value: field,
    label: t(`FLOWS.EDITOR.FIELDS.CONTACT_FIELD_${field.toUpperCase()}`),
  }))
);

const toggleNodeLabel = title => {
  const current = selectedNode.value.data.labels || [];
  selectedNode.value.data.labels = current.includes(title)
    ? current.filter(item => item !== title)
    : [...current, title];
};

// Both optional: a handoff with neither just puts the conversation back in the queue unassigned,
// which is what every handoff did before these existed.
const UNSET_OPTION = { value: '', label: '—' };

const switchTargetOptions = computed(() => [
  UNSET_OPTION,
  ...switchTargetBots.value.map(bot => ({ value: bot.id, label: bot.name })),
]);

const teamOptions = computed(() => [
  UNSET_OPTION,
  ...(teams.value || []).map(team => ({ value: team.id, label: team.name })),
]);

const assigneeOptions = computed(() => [
  UNSET_OPTION,
  ...(verifiedAgents.value || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

const attributeTargetOptions = computed(() =>
  Object.keys(ATTRIBUTE_MODELS).map(target => ({
    value: target,
    label: t(`FLOWS.EDITOR.FIELDS.TARGET_${target.toUpperCase()}`),
  }))
);

const attributeKeyOptions = computed(() => {
  const model = ATTRIBUTE_MODELS[selectedNode.value?.data?.target];
  return (customAttributes.value || [])
    .filter(attribute => attribute.attribute_model === model)
    .map(attribute => ({
      value: attribute.attribute_key,
      label: attribute.attribute_display_name || attribute.attribute_key,
    }));
});

// A graph written before this picker existed can name a key nobody ever defined - saying so is
// the only way that shows up at all, since the runtime just skips it.
const attributeKeyMissing = computed(() => {
  const key = selectedNode.value?.data?.key;
  return (
    Boolean(key) &&
    !attributeKeyOptions.value.some(option => option.value === key)
  );
});

const nodeEditDialogRef = ref(null);

// Stored in seconds to match Bots::Brains::Flow, typed in minutes because nobody thinks about a
// customer going quiet in seconds. Clearing it drops the destination too: one without the other
// is exactly what Flows::GraphValidator refuses.
const timeoutMinutes = computed({
  get: () => {
    const seconds = selectedNode.value?.data?.timeout_seconds;
    return seconds ? Math.round(seconds / 60) : '';
  },
  set: value => {
    if (!selectedNode.value) return;
    const minutes = Number(value);
    if (!minutes || minutes <= 0) {
      delete selectedNode.value.data.timeout_seconds;
      delete selectedNode.value.data.timeout_next;
      return;
    }
    selectedNode.value.data.timeout_seconds = minutes * 60;
  },
});

const openNodeEditor = id => {
  selectedNodeId.value = id;
  activeVariableField.value = null;
  nodeEditDialogRef.value?.open();
};

// An option is just its label: what the customer reads, what gets stored in the variable, and
// what a condition branches on are all the same string.
const setOptionTitle = (option, title) => {
  option.title = title;
  option.value = title;
};

// One place to grow node right-click actions from - add entries here and they show up in the
// menu automatically, no template changes needed.
const NODE_CONTEXT_MENU_ACTIONS = [
  {
    key: 'edit',
    icon: 'i-lucide-pencil',
    labelKey: 'FLOWS.EDITOR.CONTEXT_MENU.EDIT',
    run: openNodeEditor,
  },
  {
    key: 'delete',
    icon: 'i-lucide-trash',
    labelKey: 'FLOWS.EDITOR.CONTEXT_MENU.DELETE',
    danger: true,
    run: removeNode,
  },
];

const openNodeContextMenu = (event, nodeId) => {
  selectedNodeId.value = nodeId;
  nodeContextMenu.value = { nodeId, x: event.clientX, y: event.clientY };
};

const closeNodeContextMenu = () => {
  nodeContextMenu.value = null;
};

const runNodeContextMenuAction = action => {
  if (nodeContextMenu.value) action.run(nodeContextMenu.value.nodeId);
  closeNodeContextMenu();
};

const setAsStart = id => {
  graph.value.start = id;
};

const onTypeChange = (node, newType) => {
  node.type = newType;
  node.data = { ...DEFAULT_DATA[newType] };
};

const addOption = node => {
  node.data.options = node.data.options || [];
  node.data.options.push({ title: '', value: '', next: '' });
};
const removeOption = (node, index) => {
  node.data.options.splice(index, 1);
};

// What a value does to the record it is written onto when there is already one there.
// Sem segredo no grafo: ou a chamada é aberta, ou usa o token que a instalação
// já guarda. O grafo é editado na interface e exportado.
const httpAuthOptions = computed(() => [
  { value: '', label: t('FLOWS.EDITOR.FIELDS.HTTP_AUTH_NONE') },
  { value: 'erp', label: t('FLOWS.EDITOR.FIELDS.HTTP_AUTH_ERP') },
]);

const onConflictOptions = computed(() => [
  {
    value: 'fill_if_blank',
    label: t('FLOWS.EDITOR.FIELDS.ON_CONFLICT_FILL_IF_BLANK'),
  },
  { value: 'overwrite', label: t('FLOWS.EDITOR.FIELDS.ON_CONFLICT_OVERWRITE') },
  { value: 'never', label: t('FLOWS.EDITOR.FIELDS.ON_CONFLICT_NEVER') },
]);

// Mirrors Flows::FieldExtractor::TYPES - what it knows how to find inside a free-text message,
// plus `text`, which it never finds and which the field's own block has to ask for.
const collectedTypeOptions = computed(() => [
  { value: 'digits', label: t('FLOWS.EDITOR.FIELDS.COLLECTED_TYPE_DIGITS') },
  { value: 'cpf', label: t('FLOWS.EDITOR.FIELDS.COLLECTED_TYPE_CPF') },
  { value: 'keyword', label: t('FLOWS.EDITOR.FIELDS.COLLECTED_TYPE_KEYWORD') },
  { value: 'email', label: t('FLOWS.EDITOR.FIELDS.COLLECTED_TYPE_EMAIL') },
  { value: 'phone', label: t('FLOWS.EDITOR.FIELDS.COLLECTED_TYPE_PHONE') },
  { value: 'cnpj', label: t('FLOWS.EDITOR.FIELDS.COLLECTED_TYPE_CNPJ') },
  { value: 'text', label: t('FLOWS.EDITOR.FIELDS.COLLECTED_TYPE_TEXT') },
]);

// One "stored value: term, term" line per accepted answer - the graph's own shape, minus the JSON.
// A list and not an object because the order is what decides a message naming two of them, and the
// graph is stored in a jsonb column, which reorders an object's keys.
const keywordsAsText = field =>
  (field.keywords || [])
    .map(keyword => `${keyword.value}: ${(keyword.terms || []).join(', ')}`)
    .join('\n');

const setKeywords = (field, text) => {
  field.keywords = text
    .split('\n')
    .map(line => {
      const [value, terms = ''] = line.split(':');
      return {
        value: value.trim(),
        terms: terms
          .split(',')
          .map(term => term.trim())
          .filter(Boolean),
      };
    })
    .filter(keyword => keyword.value);
};

// "variavel: chave.no.json" por linha - a forma do grafo, menos o JSON.
const mapAsText = data =>
  Object.entries(data.map || {})
    .map(([variavel, chave]) => `${variavel}: ${chave}`)
    .join('\n');

const setMap = (data, text) => {
  data.map = text.split('\n').reduce((acc, line) => {
    const [variavel, ...resto] = line.split(':');
    const chave = resto.join(':').trim();
    if (variavel.trim() && chave) acc[variavel.trim()] = chave;
    return acc;
  }, {});
};

const addCollectedField = node => {
  node.data.fields = node.data.fields || [];
  node.data.fields.push({ variable: '', label: '', type: 'digits', length: 7 });
};
const removeCollectedField = (node, index) => {
  node.data.fields.splice(index, 1);
};

// The values a condition's variable can actually arrive with: the titles offered by every
// ask_choice that writes it, and the values every collect_fields keyword can store into it. A
// variable fed by set_variable/wait_for_input has no such list, so the field stays free text and
// no suggestions show.
const writtenValues = (node, variable) => {
  if (node.type === 'ask_choice' && node.data?.variable === variable) {
    return (node.data.options || []).map(option => option.title);
  }
  if (node.type === 'collect_fields') {
    return (node.data?.fields || [])
      .filter(field => field.variable === variable)
      .flatMap(field => (field.keywords || []).map(keyword => keyword.value));
  }
  return [];
};

const caseSuggestions = computed(() => {
  const variable = selectedNode.value?.data?.variable;
  if (!variable) return [];
  return [
    ...new Set(
      (graph.value?.nodes || [])
        .flatMap(node => writtenValues(node, variable))
        .filter(Boolean)
    ),
  ];
});

const addCase = node => {
  node.data.cases = node.data.cases || [];
  node.data.cases.push({ operator: 'equals', value: '', next: '' });
};
const removeCase = (node, index) => {
  node.data.cases.splice(index, 1);
};

// One named source handle per option/case (drag-to-connect target for each branch); every other
// type gets exactly one unnamed handle representing data.next. Terminal types get none.
const nodeSourceHandles = node => {
  const data = node.data || {};
  switch (node.type) {
    case 'collect_fields':
      return [
        { id: null, label: '' },
        ...(data.fields || []).flatMap((field, i) =>
          field.ask_next ? [{ id: `field-${i}`, label: field.variable }] : []
        ),
      ];
    case 'ask_choice':
      return (data.options || []).map((o, i) => ({
        id: `option-${i}`,
        label: o.title || `#${i + 1}`,
      }));
    case 'condition':
      return [
        ...(data.cases || []).map((c, i) => ({
          id: `case-${i}`,
          label: caseLabel(c) || `#${i + 1}`,
        })),
        { id: 'default', label: t('FLOWS.EDITOR.DEFAULT_CASE') },
      ];
    // A handoff only branches when it has somewhere to go with nobody available; otherwise it is
    // terminal like it always was.
    case 'http_request':
      return [
        { id: null, label: t('FLOWS.EDITOR.FIELDS.HTTP_OK') },
        { id: 'not_found', label: t('FLOWS.EDITOR.FIELDS.HTTP_NOT_FOUND') },
        { id: 'error', label: t('FLOWS.EDITOR.FIELDS.HTTP_ERROR') },
      ];
    case 'handoff':
      return data.unavailable_next
        ? [
            {
              id: 'unavailable',
              label: t('FLOWS.EDITOR.FIELDS.HANDOFF_UNAVAILABLE_LABEL'),
            },
          ]
        : [];
    case 'jump':
    case 'switch_brain':
    case 'end':
      return [];
    default:
      return [{ id: null, label: '' }];
  }
};

const insertNodeOnEdge = (type, edge) => {
  const sourceNode = graph.value.nodes.find(n => n.id === edge.source);
  const targetNode = graph.value.nodes.find(n => n.id === edge.target);
  if (!sourceNode) return;

  const sourcePos = sourceNode.position ||
    autoLayoutPositions.value[sourceNode.id] || { x: 0, y: 0 };
  const targetPos = targetNode?.position ||
    autoLayoutPositions.value[edge.target] || {
      x: sourcePos.x + 280,
      y: sourcePos.y,
    };

  const newId = addNode(type, {
    x: (sourcePos.x + targetPos.x) / 2,
    y: (sourcePos.y + targetPos.y) / 2,
  });
  const newNode = graph.value.nodes.find(n => n.id === newId);

  handleFieldSetter(sourceNode, edge.sourceHandle)(newId);
  handleFieldSetter(newNode, null)(edge.target);
};

const onPickerSelect = type => {
  if (pendingEdgeInsert.value) {
    insertNodeOnEdge(type, pendingEdgeInsert.value);
  } else {
    addNode(type);
  }
  pendingEdgeInsert.value = null;
};

const vfNodes = computed(() => {
  if (!graph.value) return [];
  return graph.value.nodes.map(n => ({
    id: n.id,
    position: n.position || autoLayoutPositions.value[n.id] || { x: 0, y: 0 },
    data: (() => {
      const name = nodeDisplayName(n);
      const preview = nodePreview(n);
      return {
        // ask_choice/condition already list their branches on the card, so an unnamed one keeps
        // its type as the heading rather than echoing those branches back.
        name: MULTI_HANDLE_TYPES.includes(n.type)
          ? n.title?.trim() || nodeTypeLabel(n.type)
          : name,
        icon: NODE_TYPE_ICONS[n.type],
        colors: NODE_TYPE_COLORS[n.type],
        preview: preview === name ? '' : preview,
        handles: nodeSourceHandles(n),
        multiHandle: MULTI_HANDLE_TYPES.includes(n.type),
      };
    })(),
    class:
      n.id === selectedNodeId.value ? 'vue-flow__node-selected-custom' : '',
  }));
});

const vfEdges = computed(() => {
  if (!graph.value) return [];
  const edges = [];
  graph.value.nodes.forEach(n => {
    nextRefsOf(n).forEach((edgeRef, i) => {
      if (!edgeRef.target) return;
      edges.push({
        id: `${n.id}->${edgeRef.target}-${i}`,
        source: n.id,
        target: edgeRef.target,
        sourceHandle: edgeRef.handleId,
        label: edgeRef.label || undefined,
        type: 'flow-edge',
        markerEnd: MarkerType.ArrowClosed,
      });
    });
  });
  return edges;
});

const { fitView, screenToFlowCoordinate } = useVueFlow();

const onConnect = connection => {
  const node = graph.value.nodes.find(n => n.id === connection.source);
  if (!node) return;
  handleFieldSetter(node, connection.sourceHandle)(connection.target);
};

const onEdgesChange = changes => {
  changes
    .filter(change => change.type === 'remove')
    .forEach(change => {
      const node = graph.value.nodes.find(n => n.id === change.source);
      if (node) handleFieldSetter(node, change.sourceHandle)('');
    });
};

const onNodesChange = changes => {
  changes
    .filter(change => change.type === 'remove')
    .forEach(change => removeNode(change.id));
};

const onNodeDragStop = ({ node }) => {
  const target = graph.value.nodes.find(n => n.id === node.id);
  if (target) target.position = { x: node.position.x, y: node.position.y };
};

const applyAutoLayout = () => {
  const positions = autoLayoutPositions.value;
  graph.value.nodes.forEach(n => {
    n.position = positions[n.id] || { x: 0, y: 0 };
  });
  nextTick(() => fitView());
};

const onPaletteDragStart = (event, type) => {
  event.dataTransfer.setData('application/x-flow-node-type', type);
  event.dataTransfer.effectAllowed = 'move';
};

const onCanvasDragOver = event => {
  if (!event.dataTransfer.types.includes('application/x-flow-node-type')) {
    return;
  }
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const onCanvasDrop = event => {
  const type = event.dataTransfer.getData('application/x-flow-node-type');
  if (!type) return;
  event.preventDefault();
  addNode(type, screenToFlowCoordinate({ x: event.clientX, y: event.clientY }));
};

const saveDraft = async (silent = false) => {
  saveStatus.value = 'saving';
  const result = await store.dispatch('flows/updateVersion', {
    flowId: flowId.value,
    versionId: workingVersionId.value,
    graph: graph.value,
  });
  if (result) {
    saveStatus.value = 'saved';
    if (!silent) useAlert(t('FLOWS.EDITOR.SAVE_SUCCESS'));
  } else {
    saveStatus.value = 'error';
    if (!silent) useAlert(t('FLOWS.EDITOR.SAVE_ERROR'));
  }
};

// n8n-style autosave: same debounce mechanics as the undo watcher (a separate watcher, since
// autosave and undo-checkpointing are different concerns with no reason to share one timer).
const AUTOSAVE_DEBOUNCE_MS = 2000;
let autosaveTimer = null;

watch(
  graph,
  () => {
    if (isRestoringHistory.value || !graph.value || !workingVersionId.value) {
      return;
    }
    saveStatus.value = 'unsaved';
    clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => saveDraft(true), AUTOSAVE_DEBOUNCE_MS);
  },
  { deep: true }
);

const publish = async () => {
  clearTimeout(autosaveTimer);
  await saveDraft(true);
  const result = await store.dispatch('flows/publishVersion', {
    flowId: flowId.value,
    versionId: workingVersionId.value,
  });
  if (result) {
    useAlert(t('FLOWS.EDITOR.PUBLISH_SUCCESS'));
    await refresh();
    loadGraphFromVersion(draftVersion.value);
  } else {
    useAlert(t('FLOWS.EDITOR.PUBLISH_ERROR'));
  }
};

const showHistory = ref(false);

const restoreVersion = async version => {
  clearTimeout(autosaveTimer);
  if (workingVersionId.value) {
    graph.value = JSON.parse(JSON.stringify(version.graph));
    graph.value.variables = graph.value.variables || [];
    resetHistory();
    await saveDraft();
  } else {
    const newVersion = await store.dispatch('flows/createVersion', {
      flowId: flowId.value,
      graph: JSON.parse(JSON.stringify(version.graph)),
    });
    if (newVersion) {
      loadGraphFromVersion(newVersion);
      useAlert(t('FLOWS.EDITOR.HISTORY.RESTORE_SUCCESS'));
    }
  }
  showHistory.value = false;
};

// The test dialog runs the draft through the same Bots::Brains::Flow production uses, over
// api/v1/accounts/:id/flows/:id/simulate. It used to re-implement that loop in JavaScript, which
// drifted: the copy never learned to interpolate {{variables}} at all. Nothing to drift now.
const simDialogRef = ref(null);
const simMessagesContainerRef = ref(null);
const simMessages = ref([]);

watch(
  simMessages,
  () => {
    nextTick(() => {
      const el = simMessagesContainerRef.value;
      if (el) el.scrollTop = el.scrollHeight;
    });
  },
  { deep: true }
);

const simVariables = ref({});
const simCursor = ref(null);
const simState = ref(null);
const simInput = ref('');
const simBusy = ref(false);

const simEnded = computed(() =>
  ['handed_off', 'completed', 'failed'].includes(simState.value)
);

// Everything the turn did that isn't a customer-visible message, rendered as a side note.
const SIM_NOTE_LABELS = {
  variable: note =>
    t('FLOWS.EDITOR.SIMULATOR.VARIABLE_SET', {
      key: note.key,
      value: note.value,
    }),
  attribute: note =>
    t('FLOWS.EDITOR.SIMULATOR.ATTRIBUTE_SET', {
      target: note.target,
      key: note.key,
      value: note.value,
    }),
  delay: note => t('FLOWS.EDITOR.SIMULATOR.DELAY', { seconds: note.seconds }),
  handoff: () => t('FLOWS.EDITOR.SIMULATOR.HANDOFF'),
  end: () => t('FLOWS.EDITOR.SIMULATOR.END'),
};

const appendSimTurn = turn => {
  turn.outputs.forEach(output => {
    if (output.type === 'message') {
      simMessages.value.push({ role: 'bot', text: output.content });
      return;
    }
    if (output.type === 'choice') {
      simMessages.value.push({
        role: 'bot',
        text: output.content,
        options: output.options,
      });
      return;
    }
    const text = SIM_NOTE_LABELS[output.kind]?.(output);
    if (text) simMessages.value.push({ role: 'system', text });
  });

  simVariables.value = turn.variables;
  simCursor.value = turn.cursor;
  simState.value = turn.state;
};

const runSimTurn = async message => {
  simBusy.value = true;
  try {
    const { data } = await FlowsAPI.simulate(flowId.value, {
      graph: graph.value,
      cursor: simCursor.value,
      variables: simVariables.value,
      message,
    });
    appendSimTurn(data);
  } catch (error) {
    simMessages.value.push({
      role: 'system',
      text: error?.response?.data?.error || t('FLOWS.EDITOR.SIMULATOR.ERROR'),
    });
    simState.value = 'failed';
  } finally {
    simBusy.value = false;
  }
};

const resetSimulation = () => {
  simMessages.value = [];
  simVariables.value = {};
  simCursor.value = null;
  simState.value = null;
  simInput.value = '';
  runSimTurn(null);
};

const openSimulator = () => {
  simDialogRef.value.open();
  resetSimulation();
};

const sendSimReply = text => {
  if (!text || simEnded.value || simBusy.value) return;
  // Once answered, an earlier choice's buttons stop being clickable - the conversation moved on.
  simMessages.value.forEach(message => {
    if (message.options) message.answered = true;
  });
  simMessages.value.push({ role: 'user', text });
  runSimTurn(text);
};

// The buttons are a shortcut, not a separate path: the backend matches a reply by index or by
// title (Bots::Channel::ReplyParser), so typing "1" gets you exactly as far as clicking does.
const selectSimOption = (message, option) => {
  if (message.answered) return;
  sendSimReply(option.title);
};

const sendSimMessage = () => {
  const input = simInput.value.trim();
  if (!input) return;
  simInput.value = '';
  sendSimReply(input);
};
</script>

<template>
  <div class="flex flex-col w-full gap-2 py-2">
    <div v-if="isLoading" class="flex justify-center py-16">
      <Spinner />
    </div>

    <div v-else-if="!graph" class="flex flex-col gap-6">
      <div class="flex items-center gap-2 min-w-0">
        <Button
          icon="i-lucide-arrow-left"
          slate
          faded
          xs
          @click="router.push(accountScopedRoute('flows_list'))"
        />
        <span class="text-base font-medium truncate text-n-slate-12">
          {{ currentFlow.name }}
        </span>
      </div>
      <div class="flex justify-center py-10">
        <div
          class="flex flex-col items-center w-full max-w-lg gap-5 px-8 py-10 text-center border rounded-2xl border-n-weak bg-n-solid-1"
        >
          <!-- A flow is created without any version, so "no draft" is either "published" or "new". -->
          <div
            v-if="publishedVersion"
            class="flex items-center justify-center size-12 rounded-xl bg-n-teal-3 outline outline-1 outline-n-teal-6"
          >
            <span class="i-lucide-circle-check size-6 text-n-teal-11" />
          </div>
          <div
            v-else
            class="flex items-center justify-center size-12 rounded-xl bg-n-blue-3 outline outline-1 outline-n-blue-6"
          >
            <span class="i-lucide-workflow size-6 text-n-blue-11" />
          </div>
          <div class="flex flex-col gap-2">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{
                publishedVersion
                  ? t('FLOWS.EDITOR.NO_DRAFT_TITLE')
                  : t('FLOWS.EDITOR.FIRST_DRAFT_TITLE')
              }}
            </h2>
            <p class="text-sm leading-relaxed text-n-slate-11">
              {{
                publishedVersion
                  ? t('FLOWS.EDITOR.NO_DRAFT')
                  : t('FLOWS.EDITOR.FIRST_DRAFT')
              }}
            </p>
          </div>
          <Button
            :label="
              publishedVersion
                ? t('FLOWS.EDITOR.CREATE_DRAFT')
                : t('FLOWS.EDITOR.START_FIRST_DRAFT')
            "
            :icon="publishedVersion ? 'i-lucide-pencil' : 'i-lucide-plus'"
            @click="createDraft"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      ref="canvasWrapperRef"
      class="flex flex-col rounded-lg border border-n-weak overflow-hidden bg-n-solid-1"
      :class="isFullscreen ? '!rounded-none !border-0' : 'min-h-[70vh]'"
      :style="isFullscreen ? undefined : { height: canvasHeight }"
    >
      <div
        class="relative z-20 flex items-center justify-between gap-2 flex-shrink-0 px-3 py-2 border-b border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-2 min-w-0">
          <Button
            icon="i-lucide-arrow-left"
            slate
            faded
            xs
            @click="router.push(accountScopedRoute('flows_list'))"
          />
          <span
            v-tooltip.bottom="currentFlow.description"
            class="text-sm font-medium text-n-slate-12 truncate"
          >
            {{ currentFlow.name }}
          </span>
          <span class="text-n-slate-8">·</span>
          <span
            class="flex items-center gap-1 text-xs flex-shrink-0"
            :class="{
              'text-n-slate-10':
                saveStatus === 'saved' || saveStatus === 'idle',
              'text-n-amber-11':
                saveStatus === 'unsaved' || saveStatus === 'saving',
              'text-n-ruby-11': saveStatus === 'error',
            }"
          >
            <span
              :class="
                saveStatus === 'saving'
                  ? 'i-lucide-loader-circle animate-spin'
                  : saveStatus === 'error'
                    ? 'i-lucide-alert-circle'
                    : 'i-lucide-check'
              "
            />
            {{ t(`FLOWS.EDITOR.SAVE_STATUS.${saveStatus.toUpperCase()}`) }}
          </span>
          <Button
            v-tooltip.bottom="t('FLOWS.EDITOR.HISTORY.OPEN')"
            icon="i-lucide-history"
            slate
            faded
            xs
            @click="showHistory = !showHistory"
          />

          <OnClickOutside v-if="showHistory" @trigger="showHistory = false">
            <div
              class="absolute top-full left-3 mt-1 z-20 w-72 max-h-96 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
            >
              <div
                v-for="version in [...flowVersions].sort(
                  (a, b) => b.version - a.version
                )"
                :key="version.id"
                class="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-n-slate-3"
              >
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5 text-n-slate-12">
                    {{ `v${version.version}` }}
                    <span
                      class="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                      :class="
                        version.published_at
                          ? 'bg-n-teal-9 text-white'
                          : 'bg-n-slate-4 text-n-slate-11'
                      "
                    >
                      {{
                        version.published_at
                          ? t('FLOWS.STATUS.PUBLISHED')
                          : t('FLOWS.STATUS.DRAFT')
                      }}
                    </span>
                  </div>
                  <div class="text-xs text-n-slate-10 truncate">
                    {{ version.notes || version.id }}
                  </div>
                </div>
                <Button
                  v-if="version.id !== workingVersionId"
                  slate
                  faded
                  xs
                  :label="t('FLOWS.EDITOR.HISTORY.RESTORE')"
                  class="flex-shrink-0"
                  @click="restoreVersion(version)"
                />
              </div>
            </div>
          </OnClickOutside>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0">
          <Button
            v-tooltip.bottom="t('FLOWS.EDITOR.VARIABLES.TITLE')"
            icon="i-lucide-braces"
            slate
            :faded="!showVariablesPanel"
            :solid="showVariablesPanel"
            sm
            @click="toggleVariablesPanel"
          />
          <Button
            v-tooltip.bottom="t('FLOWS.EDITOR.AUTO_LAYOUT')"
            icon="i-lucide-layout-grid"
            slate
            faded
            sm
            @click="applyAutoLayout"
          />
          <Button
            v-tooltip.bottom="
              t(
                isFullscreen
                  ? 'FLOWS.EDITOR.EXIT_FULLSCREEN'
                  : 'FLOWS.EDITOR.FULLSCREEN'
              )
            "
            :icon="isFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
            slate
            faded
            sm
            @click="toggleFullscreen"
          />
          <Button
            v-tooltip.bottom="
              t(
                showSidePanel
                  ? 'FLOWS.EDITOR.HIDE_PANEL'
                  : 'FLOWS.EDITOR.SHOW_PANEL'
              )
            "
            :icon="
              showSidePanel
                ? 'i-lucide-panel-right-close'
                : 'i-lucide-panel-right-open'
            "
            slate
            faded
            sm
            @click="toggleSidePanel"
          />
          <Button
            slate
            faded
            sm
            :label="t('FLOWS.EDITOR.SAVE_DRAFT')"
            icon="i-lucide-save"
            :is-loading="versionUiFlags.isUpdating"
            @click="saveDraft()"
          />
          <Button
            sm
            :label="t('FLOWS.EDITOR.PUBLISH')"
            icon="i-lucide-rocket"
            :is-loading="versionUiFlags.isPublishing"
            @click="publish"
          />
        </div>
      </div>

      <div class="relative flex flex-1 min-h-0">
        <div
          class="relative flex-1"
          @dragover="onCanvasDragOver"
          @drop="onCanvasDrop"
        >
          <VueFlow
            :nodes="vfNodes"
            :edges="vfEdges"
            fit-view-on-init
            :min-zoom="0.2"
            :max-zoom="2"
            connection-line-type="smoothstep"
            :default-edge-options="{ animated: true }"
            @connect="onConnect"
            @edges-change="onEdgesChange"
            @nodes-change="onNodesChange"
            @node-drag-stop="onNodeDragStop"
            @edge-context-menu="onEdgeContextMenu"
          >
            <Background pattern-color="#94a3b8" :gap="18" />
            <MiniMap
              pannable
              zoomable
              node-color="#7c88ff"
              mask-color="rgba(124,136,255,0.15)"
            />
            <template #edge-flow-edge="edgeProps">
              <FlowEdge v-bind="edgeProps" />
            </template>
            <template #node-default="{ id, data }">
              <!-- ask_choice/condition need visible per-option rows, so they keep a card shape -->
              <div
                v-if="data.multiHandle"
                class="relative flex flex-col gap-1.5 px-3 py-2.5 rounded-xl border bg-n-solid-2 shadow-sm cursor-pointer w-[220px] transition-shadow hover:shadow-md"
                :class="
                  id === selectedNodeId
                    ? 'border-2 border-n-blue-7'
                    : 'border-n-weak'
                "
                @click="openNodeEditor(id)"
                @contextmenu.prevent="openNodeContextMenu($event, id)"
              >
                <Handle
                  type="target"
                  :position="Position.Left"
                  class="!size-2.5 !rounded-full !bg-n-slate-9 !border-2 !border-n-solid-2"
                />

                <div class="flex items-center gap-2">
                  <span
                    class="flex-shrink-0 size-8 rounded-lg flex items-center justify-center"
                    :class="data.colors.bg"
                  >
                    <span
                      :class="[data.icon, data.colors.text]"
                      class="text-base"
                    />
                  </span>
                  <span
                    class="text-xs font-medium text-n-slate-12 truncate flex-1"
                  >
                    {{ data.name }}
                  </span>
                  <span
                    v-if="id === graph.start"
                    class="i-lucide-zap flex-shrink-0 text-n-blue-9"
                  />
                </div>

                <div class="flex flex-col gap-1 mt-0.5">
                  <div
                    v-for="h in data.handles"
                    :key="h.id"
                    class="flex items-center justify-between gap-2 pl-2 pr-1 py-1 rounded-md bg-n-slate-2 text-xs text-n-slate-12"
                  >
                    <span class="truncate">{{ h.label }}</span>
                    <Handle
                      :id="h.id"
                      type="source"
                      :position="Position.Right"
                      class="!static !transform-none size-2.5 rounded-full bg-n-blue-9 border-2 border-n-solid-2 flex-shrink-0"
                    />
                  </div>
                </div>
              </div>

              <!-- everything else: n8n-style icon tile with the label floating below it, so the
                 label doesn't affect the tile's bounding box (which is what handles center on) -->
              <div
                v-else
                class="relative flex items-center justify-center cursor-pointer"
                @click="openNodeEditor(id)"
                @contextmenu.prevent="openNodeContextMenu($event, id)"
              >
                <span
                  v-if="id === graph.start"
                  class="i-lucide-zap absolute -left-7 top-1/2 -translate-y-1/2 text-xl text-n-blue-9"
                />
                <div
                  class="relative flex items-center justify-center size-14 rounded-2xl shadow-sm transition-all"
                  :class="[
                    data.colors.bg,
                    id === selectedNodeId
                      ? 'ring-2 ring-n-blue-9'
                      : 'ring-1 ring-n-weak',
                  ]"
                >
                  <span
                    :class="[data.icon, data.colors.text]"
                    class="text-2xl"
                  />

                  <Handle
                    type="target"
                    :position="Position.Left"
                    class="!size-2.5 !rounded-full !bg-n-slate-9 !border-2 !border-n-solid-2"
                  />
                  <Handle
                    v-if="data.handles.length"
                    :id="data.handles[0].id"
                    type="source"
                    :position="Position.Right"
                    class="!size-2.5 !rounded-full !bg-n-blue-9 !border-2 !border-n-solid-2"
                  />
                </div>

                <div
                  class="absolute top-full mt-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-0.5 w-36"
                >
                  <span
                    class="text-xs font-medium text-n-slate-12 text-center line-clamp-2 w-full"
                  >
                    {{ data.name }}
                  </span>
                  <p
                    v-if="data.preview"
                    class="text-[11px] text-n-slate-10 text-center line-clamp-2 whitespace-pre-line w-full"
                  >
                    {{ data.preview }}
                  </p>
                </div>
              </div>
            </template>
          </VueFlow>

          <Button
            amber
            :label="t('FLOWS.EDITOR.SIMULATOR.OPEN')"
            icon="i-lucide-play"
            class="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 !rounded-full shadow-lg px-5"
            @click="openSimulator"
          />
        </div>

        <div
          v-if="showSidePanel"
          class="w-72 flex-shrink-0 flex flex-col gap-3 overflow-y-auto border-l border-n-weak bg-n-solid-1 p-3"
        >
          <div v-if="pendingEdgeInsert" class="flex items-center gap-2">
            <Button
              icon="i-lucide-arrow-left"
              slate
              faded
              xs
              @click="cancelEdgeInsert"
            />
            <span class="text-body-two-bold text-n-slate-12">
              {{ t('FLOWS.EDITOR.PALETTE_INSERT_TITLE') }}
            </span>
          </div>
          <span v-else class="text-body-two-bold text-n-slate-12">
            {{ t('FLOWS.EDITOR.PALETTE') }}
          </span>

          <NodePicker
            :categories="
              pendingEdgeInsert
                ? edgeInsertPickerCategories
                : fullPickerCategories
            "
            draggable
            @select="onPickerSelect"
            @dragstart="onPaletteDragStart"
          />
        </div>

        <VariablesPanel
          v-if="showVariablesPanel"
          :variables="allVariables"
          @close="showVariablesPanel = false"
          @create="addVariable"
          @rename="renameVariable"
          @delete="deleteVariable"
        />
      </div>

      <OnClickOutside v-if="edgeContextMenu" @trigger="closeEdgeContextMenu">
        <div
          class="fixed z-50 w-52 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
          :style="{
            left: `${edgeContextMenu.x}px`,
            top: `${edgeContextMenu.y}px`,
          }"
        >
          <button
            v-for="action in EDGE_CONTEXT_MENU_ACTIONS"
            :key="action.key"
            type="button"
            class="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-n-slate-3"
            :class="action.danger ? 'text-n-ruby-11' : 'text-n-slate-12'"
            @click="runEdgeContextMenuAction(action)"
          >
            <span :class="action.icon" />
            {{ t(action.labelKey) }}
          </button>
        </div>
      </OnClickOutside>

      <OnClickOutside v-if="nodeContextMenu" @trigger="closeNodeContextMenu">
        <div
          class="fixed z-50 w-44 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
          :style="{
            left: `${nodeContextMenu.x}px`,
            top: `${nodeContextMenu.y}px`,
          }"
        >
          <button
            v-for="action in NODE_CONTEXT_MENU_ACTIONS"
            :key="action.key"
            type="button"
            class="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-n-slate-3"
            :class="action.danger ? 'text-n-ruby-11' : 'text-n-slate-12'"
            @click="runNodeContextMenuAction(action)"
          >
            <span :class="action.icon" />
            {{ t(action.labelKey) }}
          </button>
        </div>
      </OnClickOutside>
    </div>

    <Dialog
      ref="nodeEditDialogRef"
      type="edit"
      width="3xl"
      overflow-y-auto
      show-cancel-button
      :show-confirm-button="false"
    >
      <template v-if="selectedNode" #title>
        <div class="group flex items-center gap-1 -ml-2">
          <input
            :value="selectedNode.title || ''"
            :placeholder="nodeAutoName(selectedNode)"
            class="reset-base min-w-0 flex-1 !px-2 !py-1 text-base font-medium rounded-md bg-transparent outline outline-1 outline-transparent hover:outline-n-weak focus:outline-n-brand text-n-slate-12 placeholder:text-n-slate-11"
            @input="selectedNode.title = $event.target.value"
          />
          <span
            class="i-lucide-pencil flex-shrink-0 text-n-slate-10 opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      </template>

      <div v-if="selectedNode" class="flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <ComboBox
            v-model="selectedNode.type"
            class="flex-1"
            :options="nodeTypeOptions"
            @update:model-value="onTypeChange(selectedNode, $event)"
          />
          <Button
            v-if="selectedNode.id !== graph.start"
            slate
            xs
            :label="t('FLOWS.EDITOR.SET_AS_START')"
            @click="setAsStart(selectedNode.id)"
          />
        </div>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('content')"
        >
          <div class="relative">
            <TextArea
              ref="contentFieldRef"
              v-model="selectedNode.data.content"
              :label="t('FLOWS.EDITOR.FIELDS.CONTENT')"
            />
            <button
              type="button"
              class="absolute top-0 right-0 flex items-center gap-1 text-[10px] font-mono text-n-slate-10 hover:text-n-blue-11 px-1.5 py-0.5 rounded"
              @click="openVariablePicker('content')"
            >
              <span class="i-lucide-braces" />
              {{ t('FLOWS.EDITOR.VARIABLES.INSERT') }}
            </button>
            <OnClickOutside
              v-if="activeVariableField === 'content'"
              @trigger="activeVariableField = null"
            >
              <div
                class="absolute z-10 right-0 top-6 w-72 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-2"
              >
                <VariablePicker
                  :variables="allVariables"
                  @select="insertVariableIntoField"
                />
              </div>
            </OnClickOutside>
          </div>
        </template>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('prompt')"
        >
          <div class="relative">
            <TextArea
              ref="promptFieldRef"
              v-model="selectedNode.data.prompt"
              :label="t('FLOWS.EDITOR.FIELDS.PROMPT')"
            />
            <button
              type="button"
              class="absolute top-0 right-0 flex items-center gap-1 text-[10px] font-mono text-n-slate-10 hover:text-n-blue-11 px-1.5 py-0.5 rounded"
              @click="openVariablePicker('prompt')"
            >
              <span class="i-lucide-braces" />
              {{ t('FLOWS.EDITOR.VARIABLES.INSERT') }}
            </button>
            <OnClickOutside
              v-if="activeVariableField === 'prompt'"
              @trigger="activeVariableField = null"
            >
              <div
                class="absolute z-10 right-0 top-6 w-72 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-2"
              >
                <VariablePicker
                  :variables="allVariables"
                  @select="insertVariableIntoField"
                />
              </div>
            </OnClickOutside>
          </div>
        </template>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('variable')"
        >
          <VariableComboBox
            v-model="selectedNode.data.variable"
            :variables="allVariables"
            :label="t('FLOWS.EDITOR.FIELDS.VARIABLE')"
            @update:model-value="addVariable"
            @open-panel="showVariablesPanel = true"
          />
        </template>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('target')"
        >
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.TARGET') }}
          </span>
          <ComboBox
            v-model="selectedNode.data.target"
            :options="attributeTargetOptions"
          />
        </template>

        <template v-if="NODE_TYPES[selectedNode.type].fields.includes('key')">
          <!-- set_variable's key names a flow variable; set_attribute's names a contact or
               conversation custom attribute, which the variables panel knows nothing about. -->
          <VariableComboBox
            v-if="selectedNode.type === 'set_variable'"
            v-model="selectedNode.data.key"
            :variables="allVariables"
            :label="t('FLOWS.EDITOR.FIELDS.KEY')"
            @update:model-value="addVariable"
            @open-panel="showVariablesPanel = true"
          />
          <template v-else>
            <span class="text-body-main text-n-slate-11">
              {{ t('FLOWS.EDITOR.FIELDS.KEY') }}
            </span>
            <ComboBox
              v-model="selectedNode.data.key"
              :options="attributeKeyOptions"
              :display-label="selectedNode.data.key"
              :has-error="attributeKeyMissing"
              :message="
                attributeKeyMissing
                  ? t('FLOWS.EDITOR.FIELDS.KEY_UNDEFINED')
                  : t('FLOWS.EDITOR.FIELDS.KEY_HINT')
              "
            />
          </template>
        </template>

        <template v-if="NODE_TYPES[selectedNode.type].fields.includes('value')">
          <div class="relative">
            <Input
              ref="valueFieldRef"
              v-model="selectedNode.data.value"
              :label="t('FLOWS.EDITOR.FIELDS.VALUE')"
            />
            <button
              type="button"
              class="absolute top-0 right-0 flex items-center gap-1 text-[10px] font-mono text-n-slate-10 hover:text-n-blue-11 px-1.5 py-0.5 rounded"
              @click="openVariablePicker('value')"
            >
              <span class="i-lucide-braces" />
              {{ t('FLOWS.EDITOR.VARIABLES.INSERT') }}
            </button>
            <OnClickOutside
              v-if="activeVariableField === 'value'"
              @trigger="activeVariableField = null"
            >
              <div
                class="absolute z-10 right-0 top-6 w-72 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-2"
              >
                <VariablePicker
                  :variables="allVariables"
                  @select="insertVariableIntoField"
                />
              </div>
            </OnClickOutside>
          </div>
        </template>

        <template v-if="NODE_TYPES[selectedNode.type].fields.includes('field')">
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.CONTACT_FIELD') }}
          </span>
          <ComboBox
            v-model="selectedNode.data.field"
            :options="contactFieldOptions"
          />
        </template>

        <template v-if="NODE_TYPES[selectedNode.type].fields.includes('note')">
          <TextArea
            v-model="selectedNode.data.note"
            :label="t('FLOWS.EDITOR.FIELDS.NOTE')"
            :message="t('FLOWS.EDITOR.FIELDS.NOTE_HINT')"
          />
        </template>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('labels')"
        >
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.LABELS') }}
          </span>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="label in accountLabels"
              :key="label.id"
              type="button"
              class="px-2 py-0.5 rounded-md text-xs border"
              :class="
                (selectedNode.data.labels || []).includes(label.title)
                  ? 'bg-n-alpha-2 border-n-brand text-n-slate-12'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="toggleNodeLabel(label.title)"
            >
              {{ label.title }}
            </button>
            <span v-if="!accountLabels.length" class="text-sm text-n-slate-11">
              {{ t('FLOWS.EDITOR.FIELDS.LABELS_EMPTY') }}
            </span>
          </div>
        </template>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('seconds')"
        >
          <Input
            v-model.number="selectedNode.data.seconds"
            type="number"
            :label="t('FLOWS.EDITOR.FIELDS.SECONDS')"
          />
        </template>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('options')"
        >
          <div class="flex flex-col gap-2">
            <span class="text-body-main text-n-slate-11">
              {{ t('FLOWS.EDITOR.FIELDS.OPTIONS') }}
            </span>
            <div
              v-for="(option, index) in selectedNode.data.options"
              :key="index"
              class="flex flex-col gap-2 p-2 rounded-md bg-n-slate-2"
            >
              <Input
                :model-value="option.title"
                :label="t('FLOWS.EDITOR.FIELDS.OPTION_TITLE')"
                @update:model-value="value => setOptionTitle(option, value)"
              />
              <Button
                icon="i-lucide-trash"
                slate
                xs
                :label="t('FLOWS.EDITOR.REMOVE')"
                @click="removeOption(selectedNode, index)"
              />
            </div>
            <Button
              icon="i-lucide-plus"
              slate
              sm
              :label="t('FLOWS.EDITOR.ADD_OPTION')"
              @click="addOption(selectedNode)"
            />
          </div>
        </template>

        <template v-if="NODE_TYPES[selectedNode.type].fields.includes('cases')">
          <div class="flex flex-col gap-2">
            <span class="text-body-main text-n-slate-11">
              {{ t('FLOWS.EDITOR.FIELDS.CASES') }}
            </span>
            <div
              v-for="(caseItem, index) in selectedNode.data.cases"
              :key="index"
              class="flex flex-col gap-2 p-2 rounded-md bg-n-slate-2"
            >
              <span class="text-body-main text-n-slate-11">
                {{ t('FLOWS.EDITOR.FIELDS.OPERATOR') }}
              </span>
              <ComboBox
                v-model="caseItem.operator"
                :options="operatorOptions"
              />
              <Input
                v-if="!UNARY_OPERATORS.includes(caseItem.operator)"
                v-model="caseItem.value"
                :label="t('FLOWS.EDITOR.FIELDS.COMPARED_VALUE')"
                :message="t('FLOWS.EDITOR.FIELDS.COMPARED_VALUE_HINT')"
              />
              <div
                v-if="caseSuggestions.length && caseItem.operator === 'equals'"
                class="flex flex-wrap items-center gap-1"
              >
                <span class="text-xs text-n-slate-10">
                  {{ t('FLOWS.EDITOR.FIELDS.EQUALS_SUGGESTIONS') }}
                </span>
                <button
                  v-for="suggestion in caseSuggestions"
                  :key="suggestion"
                  type="button"
                  class="px-2 py-0.5 rounded text-xs bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 hover:text-n-slate-12"
                  @click="caseItem.value = suggestion"
                >
                  {{ suggestion }}
                </button>
              </div>
              <Button
                icon="i-lucide-trash"
                slate
                xs
                :label="t('FLOWS.EDITOR.REMOVE')"
                @click="removeCase(selectedNode, index)"
              />
            </div>
            <Button
              icon="i-lucide-plus"
              slate
              sm
              :label="t('FLOWS.EDITOR.ADD_CASE')"
              @click="addCase(selectedNode)"
            />
          </div>
        </template>

        <template
          v-if="NODE_TYPES[selectedNode.type].fields.includes('collected')"
        >
          <div class="flex flex-col gap-2">
            <span class="text-body-main text-n-slate-11">
              {{ t('FLOWS.EDITOR.FIELDS.COLLECTED') }}
            </span>
            <div
              v-for="(field, index) in selectedNode.data.fields"
              :key="index"
              class="flex flex-col gap-2 p-2 rounded-md bg-n-slate-2"
            >
              <Input
                v-model="field.variable"
                :label="t('FLOWS.EDITOR.FIELDS.COLLECTED_VARIABLE')"
              />
              <Input
                v-model="field.label"
                :label="t('FLOWS.EDITOR.FIELDS.COLLECTED_LABEL')"
                :message="t('FLOWS.EDITOR.FIELDS.COLLECTED_LABEL_HINT')"
              />
              <ComboBox v-model="field.type" :options="collectedTypeOptions" />
              <Input
                v-if="field.type === 'digits'"
                v-model.number="field.length"
                type="number"
                min="1"
                :label="t('FLOWS.EDITOR.FIELDS.COLLECTED_LENGTH')"
              />
              <Input
                v-if="CHECKED_FIELD_TYPES.includes(field.type)"
                v-model="field.invalid_message"
                :label="t('FLOWS.EDITOR.FIELDS.COLLECTED_INVALID')"
                :message="t('FLOWS.EDITOR.FIELDS.COLLECTED_INVALID_HINT')"
              />
              <TextArea
                v-if="field.type === 'keyword'"
                :model-value="keywordsAsText(field)"
                :label="t('FLOWS.EDITOR.FIELDS.COLLECTED_KEYWORDS')"
                :message="t('FLOWS.EDITOR.FIELDS.COLLECTED_KEYWORDS_HINT')"
                @update:model-value="value => setKeywords(field, value)"
              />
              <Input
                v-model="field.map_to"
                :label="t('FLOWS.EDITOR.FIELDS.COLLECTED_MAP_TO')"
                :message="t('FLOWS.EDITOR.FIELDS.COLLECTED_MAP_TO_HINT')"
              />
              <template v-if="field.map_to">
                <span class="text-body-main text-n-slate-11">
                  {{ t('FLOWS.EDITOR.FIELDS.COLLECTED_ON_CONFLICT') }}
                </span>
                <ComboBox
                  v-model="field.on_conflict"
                  :options="onConflictOptions"
                />
              </template>
              <span class="text-body-main text-n-slate-11">
                {{ t('FLOWS.EDITOR.FIELDS.COLLECTED_ASK_NEXT') }}
              </span>
              <p class="text-sm text-n-slate-11">
                {{ t('FLOWS.EDITOR.FIELDS.COLLECTED_ASK_NEXT_HINT') }}
              </p>
              <NodeTargetPicker
                v-model="field.ask_next"
                :targets="jumpTargets"
              />
              <Button
                icon="i-lucide-trash"
                slate
                xs
                :label="t('FLOWS.EDITOR.REMOVE')"
                @click="removeCollectedField(selectedNode, index)"
              />
            </div>
            <Button
              icon="i-lucide-plus"
              slate
              sm
              :label="t('FLOWS.EDITOR.ADD_COLLECTED_FIELD')"
              @click="addCollectedField(selectedNode)"
            />
          </div>
        </template>

        <template v-if="NODE_TYPES[selectedNode.type].fields.includes('url')">
          <Input
            v-model="selectedNode.data.url"
            :label="t('FLOWS.EDITOR.FIELDS.HTTP_URL')"
            :message="t('FLOWS.EDITOR.FIELDS.HTTP_URL_HINT')"
          />
          <Input
            v-model="selectedNode.data.prefix"
            :label="t('FLOWS.EDITOR.FIELDS.HTTP_PREFIX')"
            :message="t('FLOWS.EDITOR.FIELDS.HTTP_PREFIX_HINT')"
          />
          <TextArea
            :model-value="mapAsText(selectedNode.data)"
            :label="t('FLOWS.EDITOR.FIELDS.HTTP_MAP')"
            :message="t('FLOWS.EDITOR.FIELDS.HTTP_MAP_HINT')"
            @update:model-value="value => setMap(selectedNode.data, value)"
          />
          <Input
            v-model.number="selectedNode.data.timeout"
            type="number"
            min="1"
            :label="t('FLOWS.EDITOR.FIELDS.HTTP_TIMEOUT')"
            :message="t('FLOWS.EDITOR.FIELDS.HTTP_TIMEOUT_HINT')"
          />
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.HTTP_AUTH') }}
          </span>
          <ComboBox
            v-model="selectedNode.data.auth"
            :options="httpAuthOptions"
          />
        </template>

        <template
          v-if="
            NODE_TYPES[selectedNode.type].fields.includes('missing_message')
          "
        >
          <TextArea
            v-model="selectedNode.data.missing_message"
            :label="t('FLOWS.EDITOR.FIELDS.MISSING_MESSAGE')"
            :message="t('FLOWS.EDITOR.FIELDS.MISSING_MESSAGE_HINT')"
          />
        </template>

        <template v-if="WAIT_NODE_TYPES.includes(selectedNode.type)">
          <Input
            v-model="timeoutMinutes"
            type="number"
            min="0"
            :label="t('FLOWS.EDITOR.FIELDS.TIMEOUT_MINUTES')"
            :message="t('FLOWS.EDITOR.FIELDS.TIMEOUT_HINT')"
          />
          <template v-if="selectedNode.data.timeout_seconds">
            <span class="text-body-main text-n-slate-11">
              {{ t('FLOWS.EDITOR.FIELDS.TIMEOUT_TARGET') }}
            </span>
            <NodeTargetPicker
              v-model="selectedNode.data.timeout_next"
              :targets="jumpTargets"
            />
          </template>
        </template>

        <template v-if="selectedNode.type === 'handoff'">
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.HANDOFF_TEAM') }}
          </span>
          <ComboBox
            v-model="selectedNode.data.team_id"
            :options="teamOptions"
          />
          <Input
            v-model="selectedNode.data.team"
            :label="t('FLOWS.EDITOR.FIELDS.HANDOFF_TEAM_NAME')"
            :message="t('FLOWS.EDITOR.FIELDS.HANDOFF_BY_NAME_HINT')"
          />
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.HANDOFF_ASSIGNEE') }}
          </span>
          <ComboBox
            v-model="selectedNode.data.assignee_id"
            :options="assigneeOptions"
          />
          <Input
            v-model="selectedNode.data.assignee_email"
            :label="t('FLOWS.EDITOR.FIELDS.HANDOFF_ASSIGNEE_EMAIL')"
            :message="t('FLOWS.EDITOR.FIELDS.HANDOFF_BY_NAME_HINT')"
          />
          <Input
            v-model="selectedNode.data.reason"
            :label="t('FLOWS.EDITOR.FIELDS.HANDOFF_REASON')"
            :message="t('FLOWS.EDITOR.FIELDS.HANDOFF_REASON_HINT')"
          />
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.HANDOFF_UNAVAILABLE') }}
          </span>
          <p class="text-sm text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.HANDOFF_UNAVAILABLE_HINT') }}
          </p>
          <NodeTargetPicker
            v-model="selectedNode.data.unavailable_next"
            :targets="jumpTargets"
          />
        </template>

        <template v-if="selectedNode.type === 'switch_brain'">
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.SWITCH_TARGET') }}
          </span>
          <ComboBox
            v-model="selectedNode.data.agent_bot_id"
            :options="switchTargetOptions"
          />
          <p class="text-sm text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.SWITCH_TARGET_HINT') }}
          </p>
        </template>

        <!-- Every other block's next is the line drawn on the canvas; jump is the one that
             teleports without a line, so it needs to name its destination here. -->
        <template v-if="selectedNode.type === 'jump'">
          <span class="text-body-main text-n-slate-11">
            {{ t('FLOWS.EDITOR.FIELDS.JUMP_TARGET') }}
          </span>
          <NodeTargetPicker
            v-model="selectedNode.data.next"
            :targets="jumpTargets"
          />
        </template>
      </div>
    </Dialog>

    <Dialog
      ref="simDialogRef"
      type="edit"
      :title="t('FLOWS.EDITOR.SIMULATOR.TITLE')"
      :description="t('FLOWS.EDITOR.SIMULATOR.DESCRIPTION')"
      width="xl"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-3">
        <div
          v-if="Object.keys(simVariables).length"
          class="flex flex-wrap gap-1.5"
        >
          <span
            v-for="(value, key) in simVariables"
            :key="key"
            class="text-xs font-mono px-2 py-0.5 rounded-md bg-n-slate-3 text-n-slate-11"
          >
            {{ key }}: {{ value }}
          </span>
        </div>

        <div
          ref="simMessagesContainerRef"
          class="flex flex-col gap-2 h-96 overflow-y-auto rounded-lg border border-n-weak p-3 bg-n-solid-1"
        >
          <div
            v-for="(message, index) in simMessages"
            :key="index"
            class="flex"
            :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <span
              v-if="message.role === 'system'"
              class="mx-auto text-xs text-n-slate-10 italic"
            >
              {{ message.text }}
            </span>
            <div
              v-else
              class="flex flex-col gap-1.5 max-w-[80%]"
              :class="message.role === 'user' ? 'items-end' : 'items-start'"
            >
              <div
                class="flex items-end gap-1.5"
                :class="
                  message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                "
              >
                <Avatar
                  :name="
                    message.role === 'user'
                      ? t('FLOWS.EDITOR.SIMULATOR.USER')
                      : t('FLOWS.EDITOR.SIMULATOR.BOT')
                  "
                  rounded-full
                  :size="24"
                  class="shrink-0"
                />
                <span
                  v-if="message.text"
                  class="px-3 py-2 text-sm whitespace-pre-line [overflow-wrap:break-word]"
                  :class="
                    message.role === 'user'
                      ? 'bg-n-solid-blue text-n-slate-12 rounded-br-sm rounded-bl-xl rounded-t-xl'
                      : 'bg-n-solid-iris text-n-slate-12 rounded-bl-sm rounded-br-xl rounded-t-xl'
                  "
                >
                  {{ message.text }}
                </span>
              </div>

              <div
                v-if="message.options?.length"
                class="flex flex-wrap gap-1.5 pl-[30px]"
              >
                <Button
                  v-for="(option, optionIndex) in message.options"
                  :key="optionIndex"
                  :label="option.title"
                  size="sm"
                  class="!rounded-full"
                  :variant="message.answered ? 'faded' : 'solid'"
                  :color="message.answered ? 'slate' : 'blue'"
                  :disabled="message.answered || simEnded || simBusy"
                  @click="selectSimOption(message, option)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <Input
            v-model="simInput"
            class="flex-1"
            :disabled="simEnded || simBusy"
            :placeholder="
              simEnded
                ? t('FLOWS.EDITOR.SIMULATOR.ENDED')
                : t('FLOWS.EDITOR.SIMULATOR.INPUT_PLACEHOLDER')
            "
            @keydown.enter.prevent="sendSimMessage"
          />
          <Button
            icon="i-lucide-send"
            :disabled="simEnded || simBusy"
            @click="sendSimMessage"
          />
        </div>

        <Button
          slate
          faded
          sm
          icon="i-lucide-rotate-ccw"
          :label="t('FLOWS.EDITOR.SIMULATOR.RESTART')"
          class="self-start"
          @click="resetSimulation"
        />
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
/* @vue-flow/minimap hardcodes a white background (no prop for it) - matched to the app's
   dark/light surface token here since it would otherwise stay white in dark mode. */
:deep(.vue-flow__minimap) {
  background-color: rgb(var(--solid-2));
}
</style>
